import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { CONSTANTS } from 'src/app/constants';
import { TeamsService } from 'src/app/providers/services/teams.service';
import { Market } from 'src/app/storage/models/market.model';
import { MobileDevice } from 'src/app/storage/models/mobile-device.model';
import { PeopleLocation } from 'src/app/storage/models/people-location.model';
import { Team } from 'src/app/storage/models/team.model';

const TEAM_ROLES = CONSTANTS.TEAM.ROLES;

@Component({
    selector: 'app-update-team',
    templateUrl: './update-team.component.html',
    styleUrls: ['./update-team.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class UpdateTeamComponent implements OnInit {
  form: UntypedFormGroup;

  peopleLocations: PeopleLocation[];
  mobileDevices: MobileDevice[];

  roles = [TEAM_ROLES.TECHNICIAN, TEAM_ROLES.REMOTE_SUPERVISION];

  @Input() market: Market;
  @Input() team: Team;
  @Output() closeModal = new EventEmitter<any>();

  show: boolean = true;

  constructor(
    private teamsService: TeamsService
  ) { }

  async ngOnInit(): Promise<void> {
    this.form = new UntypedFormGroup({
      teamMemberFirstName: new UntypedFormControl(this.team.teamMemberFirstName, [Validators.required]),
      teamMemberName: new UntypedFormControl(this.team.teamMemberName, [Validators.required]),
      teamMemberLanguage: new UntypedFormControl(this.team.teamMemberLanguage, [this.languageValidator()]),
      teamMemberCellularPhone: new UntypedFormControl(this.team.teamMemberCellularPhone, []),
      teamMemberEmail: new UntypedFormControl(this.team.teamMemberEmail, []),
      peoplelocationcode: new UntypedFormControl(this.team.peoplelocationcode, [Validators.required]),
      mobileDevicepk: new UntypedFormControl(this.team.mobileDevicepk, [])
    });

    this.peopleLocations = await this.teamsService.getMarketPeopleLocations(this.market.marketname);
    this.mobileDevices = await this.teamsService.getMobileDevices();

    if (this.peopleLocations == null || this.mobileDevices == null) { this.close(); }
  }

  private languageValidator(): ValidatorFn {
    return (control: AbstractControl): {[key: string]: any} | null => {
      const value = control.value;
      if (value.length > 2) { return {wrongFormat: {value: control.value}}; }
      return null;
    }
  }

  refresh(): void {
    this.show = false;
    setTimeout(() => this.show = true);
  }

  close(): void {
    this.closeModal.emit();
  }

  hasRole(rolename: string): boolean {
    return this.team.roles.find(role => role === rolename) != null;
  }

  async updateRole($event, rolename: string): Promise<void> {
    const checked: boolean = $event.target.checked;
    checked ? await this.addRole(rolename) : await this.removeRole(rolename);
  }

  async addRole(rolename: string): Promise<void> {
    const isRoleAdded = await this.teamsService.addTeamRole(this.team.teamMemberId, rolename);
    if (isRoleAdded) {
      this.team.roles.push(rolename);
    } else {
      this.refresh();
    }
  }

  async removeRole(rolename: string): Promise<void> {
    const isRoleRemoved = await this.teamsService.removeTeamRole(this.team.teamMemberId, rolename);
    if (isRoleRemoved) {
      this.team.roles = this.team.roles.filter(role => role !== rolename);
    } else {
      this.refresh();
    }
  }

  async update(): Promise<void> {
    const controls = this.form.controls;

    const peoplelocationcode = controls['peoplelocationcode'].value;
    const peoplelocationname = this.peopleLocations.find(peopleLocation => peopleLocation.peoplelocationcode === peoplelocationcode).peoplelocationname;

    const mobileDevicepk = controls['mobileDevicepk'].value;
    const mobileDevice = mobileDevicepk != null ?
                        this.mobileDevices.find(mobileDevice => mobileDevice.mobileDevicepk == mobileDevicepk) :
                        null;

    const team = new Team({
      teampk: this.team.teampk,
      teamMemberId: this.team.teamMemberId,
      teamMemberFirstName: controls['teamMemberFirstName'].value,
      teamMemberName: controls['teamMemberName'].value,
      teamMemberLanguage: controls['teamMemberLanguage'].value,
      teamMemberCellularPhone: controls['teamMemberCellularPhone'].value,
      teamMemberEmail: controls['teamMemberEmail'].value,
      peoplelocationcode: peoplelocationcode,
      peoplelocationname: peoplelocationname,
      mobileDevicepk: mobileDevice?.mobileDevicepk,
      mobileDeviceLTEProvider: mobileDevice?.mobileDeviceLTEProvider,
      mobileDeviceSimNumber: mobileDevice?.mobileDeviceSimNumber,
      deletionFlag: this.team.deletionFlag,
      roles: this.team.roles
    });

    const isTeamUpdated = await this.teamsService.updateTeam(this.market.marketname, team);

    if (isTeamUpdated) { this.closeModal.emit(team); }
  }

}
